// Uncomment the line below to enable "why-did-you-render" for debugging purposes.
// This tool helps identify unnecessary re-renders in React components, which can improve performance and simplify debugging.

// import "./wdyr"

import React from "react"
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom"
import { DarkModeProvider } from "./contexts/DarkModeContext"
import { RouterErrorHandler } from "./ErrorHandlers"
import SignIn from "./routes/SignIn"
import SignInNative from "./routes/SignInNative"
import TooManyTabs from "./routes/TooManyTabs"
import Root from "./routes/Root"
import SharedMap from "./components/maps/SharedMap"
import { Provider } from "react-redux"
import { AppStore, setupStore } from "./reducers/store"
import { Persistor, persistStore } from "redux-persist"
import { PersistGate } from "redux-persist/integration/react"
import { loadChatsArchive } from "./routes/archiveLoader"
import { OverlayToaster, Toaster as BlueprintToaster } from "@blueprintjs/core"
import { Client } from "@xmpp/client"
import { JID } from "@xmpp/jid"
import * as Sentry from "@sentry/react"
import type { SendoutQueueWorker } from "./SendoutQueueWorker"
import { isMobile } from "./utils/detectDevice"
import { type MUC } from "./xmpp_plugins/MUC"
import "./variables.css"
import AnalyzeOrderOnPageEntry from "./components/analyzeOrderOnPage/AnalyzeOrderOnPageEntry"
import { Toaster } from "@/components/ui/sonner"

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
      triggerChatChange: (chatJid: string) => void
    }
    injectedStore: string
    APP_VERSION: string
    APP_ENVIRONMENT: string
    ANDROID_APP_VERSION?: string
    appToaster: OverlayToaster
    mapToaster: BlueprintToaster | undefined
    analytics: SegmentAnalytics.AnalyticsJS
    persistor: Persistor
    XMPP: Client | undefined
    anonymousXMPP: boolean | undefined
    myJID: JID
    store: AppStore
    providedEmail: string | undefined
    sendoutQueueWorker: SendoutQueueWorker
    mucPlugin: MUC
    triggerChatChange: (chatJid: string) => void
    setSendoutQueue: (queue: string) => void
    isRouteUpdateTrackingCountdownActive?: boolean
    isRouteStartTrackingCountdownActive?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void
  }
}

window.analytics.page("Slickshift App")

if ("performance" in window) {
  const shouldTrackEntry = (entry: PerformanceEntry): boolean => {
    if (
      ["longtask", "long-animation-frame", "event", "layout-shift"].includes(
        entry.entryType,
      )
    ) {
      return false
    }

    if (entry.entryType !== "resource") {
      return true
    }

    if (RegExp("^https?://.*\\.segment\\.io/", "i").test(entry.name)) {
      // segment.io calls trigger performance API entering endless loop
      return false
    }

    if (entry instanceof PerformanceResourceTiming) {
      if (!["img", "xmlhttprequest", "fetch"].includes(entry.initiatorType)) {
        return true
      }
    }

    return false
  }

  const performanceCallback = (list: PerformanceObserverEntryList) => {
    list.getEntries().forEach((entry) => {
      if (shouldTrackEntry(entry)) {
        const payload = entry.toJSON()
        if (
          entry instanceof PerformanceMark ||
          entry instanceof PerformanceMeasure
        ) {
          payload["detail"] = entry.detail
        }
        window.analytics.track("PerformanceApi", payload)
      }
    })
  }

  const observer = new PerformanceObserver(performanceCallback)
  const observedTypes = PerformanceObserver.supportedEntryTypes.filter(
    (e) => e !== "mark",
  )
  observer.observe({ entryTypes: observedTypes })
}

window.performance.mark("StoreSetupStart")
const store = setupStore()
window.persistor = persistStore(store)
window.store = store
window.performance.mark("StoreSetupEnd")

export const routes = [
  {
    path: "/",
    element: <Root />,
    loader: loadChatsArchive(store),
    errorElement: <RouterErrorHandler />,
  },
  {
    path: "/signin",
    element: <SignIn />,
    errorElement: <RouterErrorHandler />,
  },
  {
    path: "/signin_native",
    element: <SignInNative />,
    errorElement: <RouterErrorHandler />,
  },
  {
    path: "/too-many-tabs",
    element: <TooManyTabs />,
    errorElement: <RouterErrorHandler />,
  },
  {
    path: "/shared-route/:sharedMapId",
    element: <SharedMap />,
    errorElement: <RouterErrorHandler />,
  },
  {
    path: "/analyze-order/",
    element: <AnalyzeOrderOnPageEntry />,
    errorElement: <RouterErrorHandler path="/analyze-order/" />,
  },
  {
    path: "/slick-ai/",
    element: <AnalyzeOrderOnPageEntry />,
    errorElement: <RouterErrorHandler path="/slick-ai/" />,
  },
  {
    path: "/analyzeOrder/",
    element: <AnalyzeOrderOnPageEntry />,
    errorElement: <RouterErrorHandler path="/analyzeOrder/" />,
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

const router = sentryCreateBrowserRouter(routes)

function App() {
  return (
    <div className={`body overflow-x-clip ${isMobile ? "mobile" : ""}`}>
      <DarkModeProvider>
        <OverlayToaster
          ref={(ref: OverlayToaster) => (window.appToaster = ref)}
        />
        <Toaster position="top-center" />
        <Provider store={store}>
          <PersistGate loading={null} persistor={window.persistor}>
            <RouterProvider router={router} />
          </PersistGate>
        </Provider>
      </DarkModeProvider>
    </div>
  )
}

export default App
